import React, { ReactNode, useContext, useEffect, useState } from 'react';

import RemoteAccessSessionContext, {
  getDefaultEndDate,
  getDefaultStartDate,
} from '@totem/components/services/remoteAccess/sessions/RemoteAccessSessionContext';
import {
  PersonSessionsCollection,
  RemoteAccessSessionsRequest,
} from '@totem/components/services/remoteAccess/sessions/types';
import { buildFilters } from '@totem/components/services/remoteAccess/sessions/utilities';
import { RemoteAccessSessionsInput } from '@totem/components/services/remoteAccess/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { Event } from '@totem/types/events';
import { isNotNull } from '@totem/utilities/common';
import { REMOTE_ACCESS_SESSIONS_ENDPOINT } from '@totem/utilities/endpoints';
type Props = {
  children?: ReactNode;
  refresh;
  onDataRefreshRequested;
};
const RemoteAccessSessionsContainer = ({ children }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const [input, updateInput] = useState<RemoteAccessSessionsInput>({
    from: '',
    loading: false,
    regionId: [],
    buildingId: [],
    controlSystemId: [],
    email: '',
    group: [],
    airwall: [],
    startTime: getDefaultStartDate().toISOString(),
    endTime: getDefaultEndDate().toISOString(),
    sessionPageSize: 20,
    sessionPage: 1,
    sessionSortDirection: '1',
    sessionSortField: 'eventTime',
    selectedDate: '',
  });
  const [refreshData, setRefreshData] = useState<boolean>(true);
  const [isSending, setIsSending] = useState(false);
  const [isEventSending, setEventIsSending] = useState(false);
  const [sessionData, setSessionData] = useState<PersonSessionsCollection>([]);
  const [eventData, setEventData] = useState<Event[]>([]);
  const setInput = (updated: Partial<RemoteAccessSessionsInput>) => {
    updateInput({ ...input, ...updated });

    setRefreshData(true);
  };

  useEffect(() => {
    setRefreshData(true);
  }, [userProfile.organization.id]);

  useEffect(() => {
    if (refreshData) {
      setRefreshData(false);
      if (isSending) {
        return;
      }
      const endpoint = `${REMOTE_ACCESS_SESSIONS_ENDPOINT}`;
      const payload: RemoteAccessSessionsRequest = {
        filters: buildFilters(input),
        startTime: input.startTime,
        endTime: input.endTime,
      };
      fetch(`${endpoint}`, {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(payload),
      })
        .then((res) => res.json())
        .then((result: PersonSessionsCollection) => {
          if (isNotNull(result)) {
            setSessionData(result);
          }
        })
        .then(() => {
          setIsSending(false);
        });

      setIsSending(true);
    }
  }, [refreshData, input, getApiToken]);

  useEffect(() => {
    if (refreshData) {
      setRefreshData(false);
      if (isEventSending) {
        return;
      }
      const endpoint = `${REMOTE_ACCESS_SESSIONS_ENDPOINT}/events`;
      const payload: RemoteAccessSessionsRequest = {
        filters: buildFilters(input),
        startTime: input.startTime,
        endTime: input.endTime,
      };
      fetch(`${endpoint}`, {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(payload),
      })
        .then((res) => res.json())
        .then((result: Event[]) => {
          if (isNotNull(result)) {
            setEventData(result);
          }
        })
        .then(() => {
          setEventIsSending(false);
        });

      setEventIsSending(true);
    }
  }, [refreshData, input, getApiToken]);

  return (
    <RemoteAccessSessionContext.Provider
      value={{
        input,
        setInput,
        sessionData,
        eventData,
      }}
    >
      <div>{children}</div>
    </RemoteAccessSessionContext.Provider>
  );
};
export default RemoteAccessSessionsContainer;
